import { useAvailableTenantMetadataContext } from "contexts/availableTenantMetadata";
import { sorted } from "helpers";
import { useCallback, useMemo, useState } from "react";

export const DEFAULT_DESELECTED_TENANT_NAMES: readonly string[] = ["demo"];

type ScrapEyeTenantOption = Readonly<{ id: string; longName: string }>;

export const useScrapEyeTenantSelection = () => {
  const { tenantMetadataList, isLoading } = useAvailableTenantMetadataContext();

  const scrapEyeTenants = useMemo(
    () =>
      sorted(
        tenantMetadataList.filter((tenant) =>
          tenant.feature_flags.includes("scrap_eyes")
        ),
        (tenant) => tenant.tenant_name
      ),
    [tenantMetadataList]
  );

  const tenantOptions: readonly ScrapEyeTenantOption[] = useMemo(
    () =>
      scrapEyeTenants.map((tenant) => ({
        id: tenant.tenant_name,
        longName: tenant.long_name,
      })),
    [scrapEyeTenants]
  );

  const defaultSelectedTenantIds = useMemo(
    () =>
      scrapEyeTenants
        .filter(
          (tenant) =>
            !DEFAULT_DESELECTED_TENANT_NAMES.includes(tenant.tenant_name)
        )
        .map((tenant) => tenant.tenant_name),
    [scrapEyeTenants]
  );

  const [selectedTenantIds, setSelectedTenantIds] = useState<
    readonly string[] | null
  >(null);

  const onTenantSelectionChange = useCallback((ids: readonly string[]) => {
    setSelectedTenantIds(ids);
  }, []);

  const filterTenantOption = useCallback(
    (option: ScrapEyeTenantOption, input: string) => {
      const query = input.toLowerCase();
      return (
        option.id.toLowerCase().includes(query) ||
        option.longName.toLowerCase().includes(query)
      );
    },
    []
  );

  return {
    tenantOptions,
    activeTenantIds: selectedTenantIds ?? defaultSelectedTenantIds,
    onTenantSelectionChange,
    filterTenantOption,
    isLoading,
  };
};
